import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Shield, 
  Award, 
  Users, 
  Sparkles,
  MessageSquare,
  Phone,
  Mail,
  MapPin,
  Clock,
  Factory,
  Building,
  ChevronRight,
  Quote,
  Calendar,
  CheckCircle,
  Zap,
  TrendingUp,
  Target,
  Settings,
  Layers,
  Home as HomeIcon,
  Wind,
  Lock
} from 'lucide-react';
import { productsAPI, blogsAPI } from '../services/api';
import { toast } from 'react-toastify';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch featured products
        const productsResponse = await productsAPI.getAll({ featured: true, limit: 6 });
        if (productsResponse.data.success) {
          setFeaturedProducts(productsResponse.data.data);
        }

        // Fetch recent blogs
        const blogsResponse = await blogsAPI.getAll({ limit: 3 });
        if (blogsResponse.data.success) {
          setRecentBlogs(blogsResponse.data.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load page data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const testimonialsData = [
      {
        name: 'Rajesh Kumar',
        company: 'Kumar Construction',
        text: 'Happy Steels GI door frames transformed our residential project. Unmatched quality and precision installation.',
        image: '/images/testimonial-1.jpg'
      },
      {
        name: 'Priya Sharma',
        company: 'Sharma Builders',
        text: 'Their GI ventilation systems are exceptional. Perfect airflow and zero maintenance issues in 3 years.',
        image: '/images/testimonial-2.jpg'
      },
      {
        name: 'Amit Singh',
        company: 'Singh Enterprises',
        text: 'Trusted partner for 5 years. Their GI window frames withstand harsh weather conditions perfectly.',
        image: '/images/testimonial-3.jpg'
      }
    ];
    
    setTestimonials(testimonialsData);
    
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonialsData.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: Shield,
      title: 'Galvanised Protection',
      description: 'Advanced galvanisation for superior corrosion resistance and longevity',
      color: 'from-blue-500 to-cyan-500',
      stats: '50+ Years Durability'
    },
    {
      icon: Clock,
      title: 'Precision Engineering',
      description: 'Computer-controlled manufacturing for perfect fits and finishes',
      color: 'from-green-500 to-emerald-500',
      stats: '±0.1mm Tolerance'
    },
    {
      icon: Award,
      title: 'Industry Standards',
      description: 'ISI marked products meeting all Indian construction standards',
      color: 'from-purple-500 to-pink-500',
      stats: 'ISI Certified'
    },
    {
      icon: Zap,
      title: 'Custom Solutions',
      description: 'Tailored GI products for unique architectural requirements',
      color: 'from-orange-500 to-red-500',
      stats: 'Made to Order'
    },
  ];

  const stats = [
    { icon: Users, label: 'Satisfied Clients', value: '15,000+', color: 'text-blue-600 dark:text-blue-400' },
    { icon: Factory, label: 'GI Products Made', value: '75,000+', color: 'text-green-600 dark:text-green-400' },
    { icon: Building, label: 'Buildings Equipped', value: '5,000+', color: 'text-purple-600 dark:text-purple-400' },
    { icon: Award, label: 'Years of Excellence', value: '25+', color: 'text-yellow-600 dark:text-yellow-400' },
  ];

  const productCategories = [
    {
      title: 'GI Door Frames',
      description: 'Heavy-duty galvanised iron door frames for maximum security and durability',
      image: '/images/products/door-frame-1.png',
      count: '200+ Variants',
      features: ['Anti-rust coating', 'Perfect alignment', 'Easy installation'],
      link: '/products?category=door-frames'
    },
    {
      title: 'GI Window Frames',
      description: 'Weather-resistant window frames with superior thermal performance',
      image: '/images/products/window-frame-1.png',
      count: '150+ Designs',
      features: ['Weather seal', 'Energy efficient', 'Low maintenance'],
      link: '/products?category=window-frames'
    },
    {
      title: 'Ventilation Systems',
      description: 'Advanced GI ventilation grilles and exhaust frames for optimal airflow',
      image: '/images/products/ventilation-grille-1.png',
      count: '100+ Solutions',
      features: ['Optimal airflow', 'Dust resistant', 'Easy cleaning'],
      link: '/products?category=ventilation'
    }
  ];

  const specialties = [
    {
      icon: HomeIcon,
      title: 'Residential Solutions',
      description: 'Complete GI door and window solutions for homes and apartments',
      image: '/images/products/1.png',
      benefits: ['Enhanced security', 'Long-lasting finish', 'Cost-effective']
    },
    {
      icon: Building,
      title: 'Commercial Projects',
      description: 'Large-scale GI frame installations for offices and retail spaces',
      image: '/images/products/2.png',
      benefits: ['Fire resistance', 'Low maintenance', 'Professional finish']
    },
    {
      icon: Factory,
      title: 'Industrial Applications',
      description: 'Heavy-duty GI frames for warehouses and manufacturing facilities',
      image: '/images/products/3.png',
      benefits: ['High strength', 'Chemical resistance', 'Custom sizing']
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900 transition-colors duration-300">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-4">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-300 transition-colors duration-300">Loading Amazing Content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Enhanced Hero Section - GI Products Focus */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 dark:from-gray-900 dark:via-blue-950 dark:to-indigo-950 header-spacer-lg transition-colors duration-300">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-indigo-600/10 dark:from-blue-700/20 dark:to-indigo-700/20 transition-colors duration-300"></div>
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/20 dark:bg-blue-400/30 rounded-full blur-3xl animate-pulse transition-colors duration-300"></div>
          <div className="absolute bottom-1/3 right-1/4 w-48 h-48 bg-indigo-500/20 dark:bg-indigo-400/30 rounded-full blur-2xl animate-pulse transition-colors duration-300" style={{ animationDelay: '3s' }}></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="inline-flex items-center px-6 py-3 bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-full border border-white/20 dark:border-white/10 text-white text-sm font-medium mb-8 transition-colors duration-300">
              <Shield className="w-4 h-4 mr-2" />
              Premium Galvanised Iron Solutions
            </div>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Galvanised Iron
            <span className="block bg-gradient-to-r from-blue-400 via-cyan-400 to-indigo-400 dark:from-blue-300 dark:via-cyan-300 dark:to-indigo-300 bg-clip-text text-transparent">
              Doors, Windows & Ventilation
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-gray-300 dark:text-gray-200 mb-10 max-w-4xl mx-auto leading-relaxed transition-colors duration-300">
            India's most trusted manufacturer of premium GI door frames, window systems, and ventilation solutions. 
            <span className="text-white font-semibold block mt-2">Built for strength. Designed for excellence. Galvanised for life.</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/products"
              className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-500 dark:to-indigo-500 text-white rounded-full font-semibold hover:shadow-2xl hover:shadow-blue-500/25 dark:hover:shadow-blue-400/25 transition-all duration-300 transform hover:scale-105"
            >
              <Factory className="w-5 h-5 mr-2" />
              Browse GI Products
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              className="group inline-flex items-center px-8 py-4 bg-white/10 dark:bg-white/5 backdrop-blur-sm border border-white/20 dark:border-white/10 text-white rounded-full font-semibold hover:bg-white/20 dark:hover:bg-white/10 transition-all duration-300"
            >
              <Phone className="w-5 h-5 mr-2" />
              Get Custom Quote
              <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Hero Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-4 bg-white/5 dark:bg-white/3 backdrop-blur-sm rounded-2xl border border-white/10 dark:border-white/5 transition-colors duration-300">
                <stat.icon className={`w-8 h-8 mx-auto mb-2 ${stat.color} transition-colors duration-300`} />
                <div className="text-2xl font-bold text-white">{stat.value}</div>
                <div className="text-sm text-gray-300 dark:text-gray-200 transition-colors duration-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories Section - GI Focus */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900 rounded-full text-blue-600 dark:text-blue-400 font-medium mb-4 transition-colors duration-300">
              <Layers className="w-4 h-4 mr-2" />
              Our GI Product Range
            </div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">
              Galvanised Iron Excellence
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto transition-colors duration-300">
              From residential homes to industrial complexes, our GI products deliver unmatched durability and performance
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {productCategories.map((category, index) => (
              <div key={index} className="group bg-white dark:bg-gray-700 rounded-3xl shadow-lg hover:shadow-2xl dark:shadow-gray-900/20 transition-all duration-300 overflow-hidden">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={category.image} 
                    alt={category.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = '/images/products/1.png';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <span className="inline-block px-3 py-1 bg-blue-600 dark:bg-blue-500 rounded-full text-sm font-medium">
                      {category.count}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{category.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4 transition-colors duration-300">{category.description}</p>
                  <ul className="space-y-2 mb-6">
                    {category.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">
                        <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={category.link}
                    className="group inline-flex items-center text-blue-600 dark:text-blue-400 font-semibold hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-300"
                  >
                    View Products
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-indigo-100 dark:bg-indigo-900 rounded-full text-indigo-600 dark:text-indigo-400 font-medium mb-4 transition-colors duration-300">
              <Target className="w-4 h-4 mr-2" />
              Applications & Solutions
            </div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">
              GI Products for Every Need
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto transition-colors duration-300">
              Our galvanised iron solutions serve diverse sectors with uncompromising quality and performance
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {specialties.map((specialty, index) => (
              <div key={index} className="group relative overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl dark:shadow-gray-900/30 transition-all duration-300">
                <div className="absolute inset-0">
                  <img 
                    src={specialty.image} 
                    alt={specialty.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = '/images/products/1.png';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                </div>
                <div className="relative p-8 text-white min-h-96 flex flex-col justify-end">
                  <specialty.icon className="w-12 h-12 mb-4 text-blue-400 dark:text-blue-300" />
                  <h3 className="text-2xl font-bold mb-3">{specialty.title}</h3>
                  <p className="text-gray-200 dark:text-gray-100 mb-4 leading-relaxed">{specialty.description}</p>
                  <ul className="space-y-2 mb-6">
                    {specialty.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-center text-sm text-gray-300 dark:text-gray-200">
                        <CheckCircle className="w-4 h-4 text-green-400 dark:text-green-300 mr-2" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/products"
                    className="group inline-flex items-center text-blue-400 dark:text-blue-300 font-semibold hover:text-blue-300 dark:hover:text-blue-200 transition-colors"
                  >
                    Explore Solutions
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 dark:bg-white/10 backdrop-blur-sm rounded-full text-white font-medium mb-4 transition-colors duration-300">
              <Settings className="w-4 h-4 mr-2" />
              Why Choose Our GI Products
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">
              Advanced Galvanisation Technology
            </h2>
            <p className="text-xl text-blue-100 dark:text-blue-200 max-w-3xl mx-auto transition-colors duration-300">
              State-of-the-art manufacturing processes ensure superior quality and longevity
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group relative">
                <div className="relative p-8 bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-white/20 dark:border-white/10 hover:bg-white/20 dark:hover:bg-white/10 transition-all duration-300 transform group-hover:scale-105">
                  <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mb-6`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-blue-100 dark:text-blue-200 mb-4 leading-relaxed transition-colors duration-300">{feature.description}</p>
                  <div className="inline-flex items-center px-3 py-1 bg-white/20 dark:bg-white/10 rounded-full text-white text-sm font-medium transition-colors duration-300">
                    {feature.stats}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      {featuredProducts.length > 0 && (
        <section className="py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-4 py-2 bg-green-100 dark:bg-green-900 rounded-full text-green-600 dark:text-green-400 font-medium mb-4 transition-colors duration-300">
                <Sparkles className="w-4 h-4 mr-2" />
                Featured Products
              </div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">
                Our Premium GI Collection
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto transition-colors duration-300">
                Discover our best-selling galvanised iron products, crafted with precision and built to last
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProducts.slice(0, 6).map((product) => (
                <div key={product._id} className="group bg-white dark:bg-gray-800 rounded-3xl shadow-lg hover:shadow-2xl dark:shadow-gray-900/20 transition-all duration-300 overflow-hidden">
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={product.images?.[0] || '/images/products/1.png'} 
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = '/images/products/1.png';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <span className="inline-block px-3 py-1 bg-green-600 dark:bg-green-500 rounded-full text-sm font-medium transition-colors duration-300">
                        {product.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{product.name}</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2 transition-colors duration-300">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-green-600 dark:text-green-400 transition-colors duration-300">₹{product.price}</span>
                      <button
                        onClick={() => toast.info(`Product: ${product.name}\nPrice: ₹${product.price}\nCategory: ${product.category}`)}
                        className="group inline-flex items-center text-blue-600 dark:text-blue-400 font-semibold hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-300"
                      >
                        Product Info
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/products"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-500 dark:to-emerald-500 text-white rounded-full font-semibold hover:shadow-lg hover:shadow-green-500/25 transition-all duration-300 transform hover:scale-105"
              >
                <Factory className="w-5 h-5 mr-2" />
                View All Products
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Recent Blogs Section */}
      {recentBlogs.length > 0 && (
        <section className="py-20 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-4 py-2 bg-purple-100 dark:bg-purple-900 rounded-full text-purple-600 dark:text-purple-400 font-medium mb-4 transition-colors duration-300">
                <MessageSquare className="w-4 h-4 mr-2" />
                Latest Insights
              </div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">
                Industry News & Tips
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto transition-colors duration-300">
                Stay updated with the latest trends, tips, and insights from the steel industry
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {recentBlogs.slice(0, 3).map((blog) => (
                <article key={blog._id} className="group bg-white dark:bg-gray-700 rounded-3xl shadow-lg hover:shadow-2xl dark:shadow-gray-900/20 transition-all duration-300 overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={blog.featuredImage || '/images/products/1.png'} 
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = '/images/products/1.png';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <span className="inline-block px-3 py-1 bg-purple-600 dark:bg-purple-500 rounded-full text-sm font-medium transition-colors duration-300">
                        {blog.category || 'Industry'}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-3 transition-colors duration-300">
                      <Calendar className="w-4 h-4 mr-2" />
                      {new Date(blog.createdAt).toLocaleDateString()}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 transition-colors duration-300">{blog.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3 transition-colors duration-300">{blog.excerpt || blog.content?.substring(0, 120) + '...'}</p>
                    <Link
                      to={`/blogs/${blog._id}`}
                      className="group inline-flex items-center text-purple-600 dark:text-purple-400 font-semibold hover:text-purple-700 dark:hover:text-purple-300 transition-colors duration-300"
                    >
                      Read More
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/blogs"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-500 dark:to-indigo-500 text-white rounded-full font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 transform hover:scale-105"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Read All Blogs
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-4 py-2 bg-yellow-100 dark:bg-yellow-900 rounded-full text-yellow-600 dark:text-yellow-400 font-medium mb-4 transition-colors duration-300">
                <Quote className="w-4 h-4 mr-2" />
                Client Testimonials
              </div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">
                What Our Clients Say
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto transition-colors duration-300">
                Real experiences from satisfied customers who trust our GI products for their projects
              </p>
            </div>

            <div className="relative max-w-4xl mx-auto">
              <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl dark:shadow-gray-900/30 p-8 md:p-12 transition-colors duration-300">
                <Quote className="w-12 h-12 text-blue-500 dark:text-blue-400 mb-6 transition-colors duration-300" />
                <blockquote className="text-2xl font-medium text-gray-900 dark:text-white mb-8 leading-relaxed transition-colors duration-300">
                  "{testimonials[activeTestimonial]?.text}"
                </blockquote>
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                    {testimonials[activeTestimonial]?.name?.charAt(0)}
                  </div>
                  <div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white transition-colors duration-300">
                      {testimonials[activeTestimonial]?.name}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400 transition-colors duration-300">
                      {testimonials[activeTestimonial]?.company}
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial Navigation */}
              <div className="flex justify-center mt-8 space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === activeTestimonial 
                        ? 'bg-blue-600 dark:bg-blue-500 w-8' 
                        : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 transition-colors duration-300">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Project?
          </h2>
          <p className="text-xl text-blue-100 dark:text-blue-200 mb-8 max-w-2xl mx-auto transition-colors duration-300">
            Get premium GI door frames, windows, and ventilation solutions tailored to your needs. 
            Contact our experts for a free consultation and quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="group inline-flex items-center px-8 py-4 bg-white dark:bg-gray-100 text-blue-600 dark:text-blue-700 rounded-full font-semibold hover:bg-gray-50 dark:hover:bg-gray-200 transition-colors duration-300"
            >
              <Phone className="w-5 h-5 mr-2" />
              Get Free Quote
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/products"
              className="group inline-flex items-center px-8 py-4 bg-white/20 dark:bg-white/10 backdrop-blur-sm border border-white/30 dark:border-white/20 text-white rounded-full font-semibold hover:bg-white/30 dark:hover:bg-white/20 transition-all duration-300"
            >
              <Factory className="w-5 h-5 mr-2" />
              Browse Catalog
              <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
